import React, { useEffect, useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./PatientMain.css";
import { FaCheck} from "react-icons/fa6";
import axios from "axios";
import logo from '../assets/CAROLINA.png';
import DatePicker from 'rsuite/DatePicker';
import 'rsuite/DatePicker/styles/index.css';
import { BsFileEarmarkCheck, BsXLg, BsFolder, BsFolderFill, BsFiles, BsTrash, BsCloudUpload, BsChevronDown, BsChevronRight, BsCheckCircle, BsXCircle, BsInfoCircle } from "react-icons/bs";
import { makeZip } from 'client-zip';
import { API_URLS } from '../config/api';
import { useDropzone } from 'react-dropzone';




const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

// Progress bar is split into two contiguous phases so it never jumps backwards:
// preparing the zip fills 0-PREP_PORTION%, uploading fills the rest.
const PREP_PORTION = 35;
const UPLOAD_PORTION = 100 - PREP_PORTION;

const MB = 1024 * 1024;

// At/below this, upload in one PUT (simple, and a restart-on-failure is cheap).
// Above it, use parallel multipart so a dropped chunk retries on its own.
const MULTIPART_THRESHOLD = 50 * MB;

// Chunk size for multipart. 8MB is a good throughput/overhead balance; grow it
// only for very large files to stay under S3's 10,000-part cap.
const chunkSizeFor = (fileSize) => {
    let size = 8 * MB;
    size = Math.max(size, Math.ceil(fileSize / 10000));
    return Math.ceil(size / MB) * MB;
};


const getFileExtension = (name) => {
    const dot = name.lastIndexOf('.');
    return dot !== -1 ? name.slice(dot).toLowerCase() : '';
};

const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
        return `File exceeds 5GB limit: ${file.name}`;
    }
    return null;
};

const validateZipMagicBytes = async (file) => {
    const header = await file.slice(0, 4).arrayBuffer();
    const bytes = new Uint8Array(header);
    const isZip = bytes[0] === 0x50 && bytes[1] === 0x4B &&
        (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07) &&
        (bytes[3] === 0x04 || bytes[3] === 0x06 || bytes[3] === 0x08);
    return isZip;
};

const sanitizePathName = (name) => {
    return name
        .replace(/\.\./g, '')
        .replace(/[/\\%<>:"|?*\x00-\x1F]/g, '_')
        .replace(/^\.+/, '')
        .replace(/_+/g, '_')
        .trim();
};

// Build the zip's S3 filename: "<patient>_<practitioner>.zip" with whitespace
// replaced by underscores so the object key never contains spaces (spaces break
// presigned-GET downloads).
const buildZipName = (patient, practitioner) =>
    (sanitizePathName(patient) + '_' + sanitizePathName(practitioner.replace(/Dr\. |dr\. |Dr |dr /g, '')) + '.zip')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_');

const sanitizeInput = (value) => {
    if (!value) return '';
    return value
        .replace(/<[^>]*>/g, '')
        .replace(/[<>"'`;()]/g, '')
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
        .trim();
};

function PatientReferralForm() {
    const navigate = useNavigate();
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const showToast = useCallback((message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: '', type: '' });
        }, 4000);
    }, []);
    const [ip, setIP] = React.useState('');
    const [dimensions, setDimensions ] = React.useState(window);
    const [progress, setProgress] = React.useState(0);
    const [progressLabel, setProgressLabel] = React.useState('');
    const [file, setFile] = React.useState(null);
    const [fileType, setFileType] = React.useState(null);
    const [uploadID, setUploadID] = React.useState('');
    const [patient_scan_date, setPatientScanDate] = React.useState(null);
    const [patient_dob_date, setPatientDOBDate] = React.useState(null);
    const [dobManuallySet, setDobManuallySet] = React.useState(false);
    const [scanDateManuallySet, setScanDateManuallySet] = React.useState(false);
    const [regions_of_interest, setRegionsOfInterest] = React.useState('');
    const [practice_name, setPracticeName] = React.useState('');
    const [practitioner_name, setPractitionerName] = React.useState('');
    const [contact_email, setContactEmail] = React.useState('');
    const [patient_name, setPatientName] = React.useState('');

    const drop = React.useRef(null);
    const abortControllerRef = useRef(null);
    const [dragging, setDragging] = React.useState(false);
    const [droppedFiles, setDroppedFiles] = React.useState([]);
    const [droppedFilesDict, setDroppedFilesDict] = React.useState({});
    const [uploadType, setUploadType] = React.useState('');
    
    const handleFileBrowse = (e) => {
        e.preventDefault();
        setUploadType('file');
    }

    const handleFolderBrowse = (e) => {
        e.preventDefault();
        setUploadType('folder');
    }

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const files = [...e.dataTransfer.files];
        if (files && files.length) {
            const rejected = files.filter(f => validateFile(f));
            const accepted = files.filter(f => !validateFile(f));
            if (rejected.length) {
                showToast(`Removed ${rejected.length} file(s) that exceed the 5GB size limit.`, "error");
            }
            if (accepted.length) {
                setDroppedFiles(accepted);
                setFileType(accepted[0].type);
            }
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };

    const handleFolderInput = (event) => {
        const files = [...event.target.files];

        if (!files.length) return;
        let temp_dict = {};
        files.forEach((item) => {
            let parent_name = item.webkitRelativePath.split('/')[0];
            if (temp_dict[parent_name]) {
                temp_dict[parent_name].push(item.webkitRelativePath);
            } else {
                temp_dict[parent_name] = [item.webkitRelativePath];
            }
        });

        if (Object.keys(temp_dict).length > 0) {
            setDroppedFilesDict(prev => ({
                ...prev,
                ...temp_dict
            }));
        }

        event.target.value = null;

        if (files && files.length) {
            const rejected = files.filter(f => validateFile(f));
            const accepted = files.filter(f => !validateFile(f));
            if (rejected.length) {
                showToast(`Removed ${rejected.length} file(s) that exceed the 5GB size limit.`, "error");
            }
            if (accepted.length) {
                setDroppedFiles(prevFiles => [...prevFiles, ...accepted]);
                setFileType(accepted[0].type);
            }
        }
    };

    const handleFileInput = (event) => {
        const files = [...event.target.files];
        event.target.value = null; // allow re-selecting the same file
        if (files.length) onDrop(files);
    };

    const fileRemove = (folder) => {
        setDroppedFilesDict(prev => {
            const { [folder]: _, ...rest } = prev;
            return rest;
        });

        setDroppedFiles(prev => prev.filter(f => {
            const root = (f.webkitRelativePath || f.name).split('/')[0];
            return root !== folder;
        }));
    }

    const fileRemoveSingle = (fullPath) => {
        setDroppedFiles(prev => prev.filter(f => (f.webkitRelativePath || f.path || f.name) !== fullPath));

        setDroppedFilesDict(prev => {
            const updated = {};
            for (const [folder, paths] of Object.entries(prev)) {
                const filtered = paths.filter(p => p !== fullPath);
                if (filtered.length > 0) {
                    updated[folder] = filtered;
                }
            }
            return updated;
        });
    }

    const fileRemoveAll = () => {
        setDroppedFilesDict({});
        setDroppedFiles([]);
    }

    function generateRandomString(length) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        const randomValues = new Uint32Array(length);
        crypto.getRandomValues(randomValues);
        for ( let i = 0; i < length; i++ ) {
            result += characters.charAt(randomValues[i] % charactersLength);
        }
        return result;
    }

    useEffect(() => {
        axios.post("https://ipinfo.io", null, { timeout: 5000 })
        .then((response) => {
            setIP(response.data.ip);
        })
        .catch(() => {
            setIP('unavailable');
        });
    }, []);

    const [genderValue, setGenderValue] = React.useState('');
    const genderOptions = [
        { label: 'No Selection', value: 'No Selection' },
        { label: 'Male', value: 'Male' },
        { label: 'Female', value: 'Female' },
    ];
    const handleGenderChange = (event) => {
        setGenderValue(event.target.value);
    };

    const [ethnicityValue, setEthnicityValue] = React.useState('');
    const ethnicityOptions = [
        { label: 'No Selection', value: 'No Selection' },
        { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
        { label: 'White (Not Hispanic or Latino) ', value: 'White (Not Hispanic or Latino)' },
        { label: 'Black or African American (Not Hispanic or Latino) ', value: 'Black or African American (Not Hispanic or Latino)' },
        { label: 'Native Hawaiian or Other Pacific Islander (Not Hispanic or Latino)', value: 'Native Hawaiian or Other Pacific Islander (Not Hispanic or Latino)' },
        { label: 'Asian (Not Hispanic or Latino)', value: 'Asian (Not Hispanic or Latino)' },
        { label: 'American Indian or Alaska Native (Not Hispanic or Latino)', value: 'American Indian or Alaska Native (Not Hispanic or Latino)' },
        { label: 'Two or More Races (Not Hispanic or Latino)', value: 'Two or More Races (Not Hispanic or Latino)' },
    ];
    const handleEthnicityChange = (event) => {
        setEthnicityValue(event.target.value);
    };

    const handlePatientDOBDateChange = (d) => {
        setPatientDOBDate(d);
        setDobManuallySet(true);
    }

    const handlePatientScanDateChange = (d) => {
        setPatientScanDate(d);
        setScanDateManuallySet(true);
    }

    const [studyValue, setStudyValue] = React.useState('Report');
    const studyOptions = [
        { label: 'Report', value: 'Report' },
        { label: 'SimPlant Study', value: 'SimPlant Study' },
    ];
    const handleStudyChange = (event) => {
        setStudyValue(event.target.value);
    };

    const [studyPlusValue, setStudyPlusValue] = React.useState('Report 2-3 Days');
    const studyPlusOptions = [
        { label: 'No Add-ons', value: 'No Add-ons' },
        { label: '+$25 Endo/Implant Evaluation 3 or more teeth', value: '+$25 Endo/Implant Evaluation 3 or more teeth' },
        { label: '+$25 Comparison Report', value: '+$25 Comparison Report' },
        { label: '+$25 Rush Case (less than 24 hours)', value: '+$25 Rush Case (less than 24 hours)' },
    ];
    const handleStudyPlusChange = (event) => {
        setStudyPlusValue(event.target.value);
    };

    const [paymentMethod, setPaymentMethodValue] = React.useState('MC/Visa');
    const paymentMethodOptions = [
        { label: 'Credit Card', value: 'Credit Card' },
        { label: 'Payment Link', value: 'Payment Link' },
        { label: 'Invoice', value: 'Invoice' }
    ];
    const handlePaymentMethod = (event) => {
        setPaymentMethodValue(event.target.value);
    };

    const handleRegionsChange = (event) => {
        setRegionsOfInterest(event.target.value);
    };

    // Check if all required fields are filled
    const isFormValid = () => {
        return (
            practice_name.trim() !== '' &&
            practitioner_name.trim() !== '' &&
            contact_email.trim() !== '' &&
            patient_name.trim() !== '' &&
            droppedFiles.length > 0
        );
    };

    const cancelUpload = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setProgress(0);
        setProgressLabel('');
        showToast("Upload cancelled.", "info");
    };

    const waitForOnline = () => {
        return new Promise((resolve) => {
            if (navigator.onLine) { resolve(); return; }
            const handler = () => { window.removeEventListener('online', handler); resolve(); };
            window.addEventListener('online', handler);
        });
    };

    const isCancel = (error) =>
        axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.message === 'canceled';

    // Retry only on transient failures: network errors, timeouts, stalls, and
    // 5xx/408/429. A definitive 4xx (bad signature, validation, expired URL)
    // won't fix itself on retry, so fail fast instead of burning attempts.
    const isRetryable = (error) => {
        if (isCancel(error)) return false;
        const status = error.response?.status;
        if (status === undefined) return true;
        return status >= 500 || status === 408 || status === 429;
    };

    // POST to the upload API with retry/backoff (the sign + finalize calls).
    const apiPost = async (body, signal, { timeout = 30000, retries = 3 } = {}) => {
        let lastError;
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await axios.post(API_URLS.UPLOAD, {
                    headers: { "Content-Type": "multipart/form-data" },
                    body,
                }, { timeout, signal });
            } catch (error) {
                lastError = error;
                if (!isRetryable(error) || attempt === retries) throw error;
                await new Promise(r => setTimeout(r, Math.min(1000 * 2 ** (attempt - 1), 8000)));
            }
        }
        throw lastError;
    };

    // Hard backstop timeout for a PUT of `bytes`: ~1 min per 10MB (~1.3 MB/s
    // floor), min 60s, capped at 15 min. The stall guard below usually fires
    // first; this only catches pathological cases.
    const putTimeoutFor = (bytes) => Math.min(
        15 * 60 * 1000,
        Math.max(60 * 1000, Math.ceil(bytes / (10 * 1024 * 1024)) * 60 * 1000)
    );

    // Abort a PUT if no upload bytes move for this long. The observed failure
    // mode is a connection that stalls mid-transfer (bytes stop, no error) until
    // the full timeout elapses. Detecting the stall fast lets us retry on a fresh
    // socket, which typically clears it — without penalizing slow-but-steady links.
    const STALL_MS = 20000;

    // PUT a blob with stall detection layered on top of the global cancel signal.
    const putBlob = async (url, blob, contentType, onProgress, globalSignal, hardTimeout) => {
        const ac = new AbortController();
        const onGlobalAbort = () => ac.abort();
        if (globalSignal) {
            if (globalSignal.aborted) ac.abort();
            else globalSignal.addEventListener('abort', onGlobalAbort);
        }
        let lastProgress = Date.now();
        let stalled = false;
        let bodySent = false;
        const stallTimer = setInterval(() => {
            // Only watch for stalls WHILE the body is uploading. Once the body is
            // fully sent, the request is legitimately waiting for S3's response —
            // no more progress events fire, and the hard timeout covers that wait.
            if (!bodySent && Date.now() - lastProgress > STALL_MS) {
                stalled = true;
                ac.abort();
            }
        }, 2000);
        try {
            return await axios.put(url, blob, {
                headers: { 'Content-Type': contentType },
                transformRequest: [(d) => d],
                timeout: hardTimeout,
                signal: ac.signal,
                onUploadProgress: (e) => {
                    lastProgress = Date.now();
                    if (e.total && e.loaded >= e.total) bodySent = true;
                    if (onProgress) onProgress(e);
                },
            });
        } catch (error) {
            // A stall-triggered abort must look like a retryable error, not a
            // user cancel (which would tear down the whole upload).
            if (stalled && !(globalSignal && globalSignal.aborted)) {
                const e = new Error('Upload stalled (no progress for 20s)');
                e.code = 'ERR_STALLED';
                throw e;
            }
            throw error;
        } finally {
            clearInterval(stallTimer);
            if (globalSignal) globalSignal.removeEventListener('abort', onGlobalAbort);
        }
    };

    const uploadFile = async (fileId, patient_name, practitioner_name, zippedFiles) => {
        if (!zippedFiles) {
            showToast("Please add files to complete request", "error");
            return false;
        }

        const fileName = buildZipName(patient_name, practitioner_name);

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        try {
            if (zippedFiles.size > MULTIPART_THRESHOLD) {
                await uploadMultipart(fileId, fileName, zippedFiles, signal);
            } else {
                await uploadSinglePut(fileId, fileName, zippedFiles, signal);
            }
            return true;
        } catch (error) {
            if (isCancel(error)) return false;
            setProgressLabel('Upload failed');
            showToast(friendlyUploadError(error), "error");
            return false;
        } finally {
            abortControllerRef.current = null;
        }
    };

    // Map raw axios/network errors to plain language for users (#5).
    const friendlyUploadError = (error) => {
        const status = error.response?.status;
        if (error.code === 'ERR_STALLED' || error.code === 'ECONNABORTED') {
            return "The connection stalled and the upload couldn't finish. Please check your internet and try again.";
        }
        if (error.code === 'ERR_NETWORK') {
            return "Network error during upload. Please check your connection and try again.";
        }
        if (status === 403) return "The upload link was rejected. Please refresh the page and try again.";
        if (status && status >= 500) return "The server had a problem during upload. Please try again in a moment.";
        return error.message || "Error uploading file. Please try again.";
    };

    const uploadSinglePut = async (fileId, fileName, zippedFiles, signal) => {
        const MAX_RETRIES = 4;
        const putTimeout = putTimeoutFor(zippedFiles.size);

        setProgressLabel('Requesting upload URL...');
        const signResponse = await apiPost({ fileID: fileId, fileName, fileType: 'application/zip', fileOperation: "getSignedUrl" }, signal);

        const signedUrl = signResponse.data.body ?? signResponse.data;
        if (!signedUrl || typeof signedUrl !== 'string') {
            throw new Error('Could not get an upload URL. Please try again.');
        }

        let lastError;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                if (!navigator.onLine) {
                    setProgressLabel('Waiting for connection...');
                    await waitForOnline();
                }
                setProgressLabel('Uploading...');
                let lastPct = -1;
                const startedAt = Date.now();
                const putResponse = await putBlob(signedUrl, zippedFiles, 'application/zip', (e) => {
                    const pct = Math.round((e.loaded * 100) / (e.total || zippedFiles.size));
                    if (pct !== lastPct) {
                        lastPct = pct;
                        setProgress(PREP_PORTION + Math.round((pct / 100) * UPLOAD_PORTION));
                    }
                }, signal, putTimeout);
                if (putResponse.status === 200 || putResponse.status === 204) {
                    logThroughput(zippedFiles.size, startedAt);
                    setProgress(100);
                    return;
                }
                throw new Error('Upload failed with status ' + putResponse.status);
            } catch (error) {
                if (isCancel(error)) throw error;
                lastError = error;
                console.error(`[upload] single PUT attempt ${attempt} failed`, { message: error.message, code: error.code, status: error.response?.status });
                if (!isRetryable(error)) break;
                if (attempt < MAX_RETRIES) {
                    setProgressLabel(`Upload failed, retrying... (${attempt + 1}/${MAX_RETRIES})`);
                    await new Promise(r => setTimeout(r, Math.min(1000 * 2 ** (attempt - 1), 8000)));
                }
            }
        }
        throw lastError || new Error('Upload failed after all retries');
    };

    const uploadMultipart = async (fileId, fileName, zippedFiles, signal) => {
        const MAX_RETRIES = 3;
        const CONCURRENCY = 3;
        const fileSize = zippedFiles.size;
        const CHUNK_SIZE = chunkSizeFor(fileSize);
        const startedAt = Date.now();

        const chunks = [];
        for (let i = 0; i < Math.ceil(fileSize / CHUNK_SIZE); i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, fileSize);
            chunks.push({ index: i, blob: zippedFiles.slice(start, end), size: end - start });
        }

        // Step 1: open the multipart upload.
        setProgressLabel('Requesting upload URL...');
        const initResponse = await apiPost({ fileID: fileId, fileName, fileOperation: "createUploadID" }, signal);
        const initData = initResponse.data.body ?? initResponse.data;
        let uploadId;
        try {
            uploadId = typeof initData === 'object' ? initData.UploadId : JSON.parse(initData).UploadId;
        } catch (_) { throw new Error('Failed to start upload'); }
        if (!uploadId) throw new Error('Failed to start upload - no UploadId');

        // Aggregate progress across all in-flight chunks so the bar moves
        // immediately and never looks frozen (the old multipart's main UX flaw).
        const completedBytes = new Map(); // index -> fully uploaded bytes
        const inflightBytes = new Map();  // index -> bytes uploaded so far
        const renderProgress = () => {
            let loaded = 0;
            completedBytes.forEach(v => { loaded += v; });
            inflightBytes.forEach(v => { loaded += v; });
            setProgress(PREP_PORTION + Math.round((loaded / fileSize) * UPLOAD_PORTION));
        };
        const setChunkLabel = () => setProgressLabel(`Uploading... (${completedBytes.size}/${chunks.length} parts)`);
        setChunkLabel();

        const uploadChunk = async (chunk, attempt = 1) => {
            try {
                if (!navigator.onLine) {
                    setProgressLabel('Waiting for connection...');
                    await waitForOnline();
                }
                const signResponse = await apiPost({ fileID: fileId, fileName, fileOperation: "getSignedUrl", uploadId, partNumber: chunk.index + 1 }, signal, { retries: 2 });
                const signedUrl = signResponse.data.body ?? signResponse.data;
                if (!signedUrl || typeof signedUrl !== 'string') throw new Error(`No signed URL for part ${chunk.index + 1}`);

                await putBlob(signedUrl, chunk.blob, 'application/octet-stream', (e) => {
                    inflightBytes.set(chunk.index, e.loaded);
                    renderProgress();
                }, signal, putTimeoutFor(chunk.size));

                inflightBytes.delete(chunk.index);
                completedBytes.set(chunk.index, chunk.size);
                renderProgress();
                setChunkLabel();
            } catch (error) {
                inflightBytes.delete(chunk.index);
                if (isCancel(error)) throw error;
                console.error(`[upload] part ${chunk.index + 1} attempt ${attempt} failed`, { message: error.message, code: error.code, status: error.response?.status });
                if (isRetryable(error) && attempt < MAX_RETRIES) {
                    await new Promise(r => setTimeout(r, Math.min(1000 * 2 ** (attempt - 1), 8000)));
                    return uploadChunk(chunk, attempt + 1);
                }
                throw error.response ? error : new Error(`Part ${chunk.index + 1} failed after ${attempt} attempt(s)`);
            }
        };

        // Step 2: upload chunks with a bounded worker pool (keeps CONCURRENCY
        // streams continuously in flight without flooding the connection pool).
        let cursor = 0;
        const worker = async () => {
            while (cursor < chunks.length) {
                await uploadChunk(chunks[cursor++]);
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, () => worker()));

        // Step 3: finalize (Lambda lists parts server-side and assembles them).
        setProgressLabel('Finalizing...');
        const completeResponse = await apiPost({ fileID: fileId, fileName, uploadId, fileOperation: "completeMultipartUpload" }, signal, { timeout: 60000 });
        const completeData = completeResponse.data.body ?? completeResponse.data;
        let result;
        try { result = typeof completeData === 'string' ? JSON.parse(completeData) : completeData; }
        catch (_) { throw new Error('Failed to parse completion response'); }

        if (completeResponse.status === 200 && !result.error) {
            logThroughput(fileSize, startedAt);
            setProgress(100);
        } else {
            throw new Error('Failed to finalize upload: ' + (result.error || completeResponse.status));
        }
    };

    // Upload a single already-built multipart part (sign + PUT) with retry.
    // Used by the streaming path, which produces parts one at a time.
    const uploadOnePart = async (fileId, fileName, uploadId, partNumber, partBlob, signal, onInflight) => {
        const MAX_RETRIES = 3;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                if (!navigator.onLine) {
                    setProgressLabel('Waiting for connection...');
                    await waitForOnline();
                }
                const signResponse = await apiPost({ fileID: fileId, fileName, fileOperation: "getSignedUrl", uploadId, partNumber }, signal, { retries: 2 });
                const signedUrl = signResponse.data.body ?? signResponse.data;
                if (!signedUrl || typeof signedUrl !== 'string') throw new Error(`No signed URL for part ${partNumber}`);

                await putBlob(signedUrl, partBlob, 'application/octet-stream', (e) => {
                    if (onInflight) onInflight(e.loaded);
                }, signal, putTimeoutFor(partBlob.size));
                return;
            } catch (error) {
                if (isCancel(error)) throw error;
                console.error(`[upload] part ${partNumber} attempt ${attempt} failed`, { message: error.message, code: error.code, status: error.response?.status });
                if (isRetryable(error) && attempt < MAX_RETRIES) {
                    await new Promise(r => setTimeout(r, Math.min(1000 * 2 ** (attempt - 1), 8000)));
                    continue;
                }
                throw error.response ? error : new Error(`Part ${partNumber} failed after ${attempt} attempt(s)`);
            }
        }
    };

    // Zip a folder and stream it straight into S3 multipart parts. Crucially, we
    // never materialize the whole archive (or any whole input file) in memory:
    // client-zip streams from each File and we buffer up to PART_SIZE, upload that
    // one part, free it, then pull more. Peak memory is ~one part (not the full
    // 3-4GB), which is what was OOM-crashing low-RAM tabs.
    const streamZipToMultipart = async (fileId, fileName, uploadId, signal, onProgress) => {
        const PART_SIZE = 16 * MB;

        // client-zip reads each File lazily via its .stream() (disk-backed), so no
        // input file is ever fully loaded into memory. Pulling from the reader one
        // chunk at a time also applies natural backpressure: the zip only advances
        // as fast as we upload, keeping peak memory to ~one part.
        const inputs = droppedFiles.map((f) => ({
            input: f,
            name: f.webkitRelativePath || f.path || f.name,
            lastModified: f.lastModified ? new Date(f.lastModified) : new Date(),
            size: f.size,
        }));
        const reader = makeZip(inputs).getReader();

        let buffered = [];
        let bufferedLen = 0;
        let partNumber = 0;
        let uploadedBytes = 0;

        const flushPart = async () => {
            const partBlob = new Blob(buffered);
            buffered = [];
            bufferedLen = 0;
            partNumber += 1;
            setProgressLabel(`Compressing & uploading... (${partNumber} part${partNumber === 1 ? '' : 's'})`);
            const base = uploadedBytes;
            await uploadOnePart(fileId, fileName, uploadId, partNumber, partBlob, signal, (loaded) => onProgress(base + loaded));
            uploadedBytes += partBlob.size;
            onProgress(uploadedBytes);
        };

        try {
            for (;;) {
                if (signal?.aborted) {
                    const e = new Error('canceled');
                    e.code = 'ERR_CANCELED';
                    throw e;
                }
                const { done, value } = await reader.read();
                if (done) break;
                buffered.push(value);
                bufferedLen += value.length;
                if (bufferedLen >= PART_SIZE) {
                    await flushPart();
                }
            }
            // Flush whatever remains (also covers a folder small enough to produce
            // a single sub-PART_SIZE part — valid as the last part).
            if (bufferedLen > 0 || partNumber === 0) {
                await flushPart();
            }
        } finally {
            try { reader.releaseLock(); } catch (_) {}
        }
        return partNumber;
    };

    // Streaming upload wrapper for the zip-a-folder case (mirrors uploadFile's
    // abort/error/toast handling, but zips and uploads at the same time).
    const uploadFolderStreaming = async (fileId, patient_name, practitioner_name) => {
        const fileName = buildZipName(patient_name, practitioner_name);
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        const startedAt = Date.now();
        const estimatedTotal = droppedFiles.reduce((a, f) => a + (f.size || 0), 0) || 1;

        try {
            setProgressLabel('Requesting upload URL...');
            const initResponse = await apiPost({ fileID: fileId, fileName, fileOperation: "createUploadID" }, signal);
            const initData = initResponse.data.body ?? initResponse.data;
            let uploadId;
            try { uploadId = typeof initData === 'object' ? initData.UploadId : JSON.parse(initData).UploadId; }
            catch (_) { throw new Error('Failed to start upload'); }
            if (!uploadId) throw new Error('Failed to start upload - no UploadId');

            // Floor at 1: progress === 0 is the "show the form" sentinel, so a 0%
            // (bytes not yet moved) must never be set or the form flashes back.
            const renderProgress = (bytes) => {
                setProgress(Math.max(1, Math.min(100, Math.round((bytes / estimatedTotal) * 100))));
            };

            await streamZipToMultipart(fileId, fileName, uploadId, signal, renderProgress);

            setProgressLabel('Finalizing...');
            const completeResponse = await apiPost({ fileID: fileId, fileName, uploadId, fileOperation: "completeMultipartUpload" }, signal, { timeout: 60000 });
            const completeData = completeResponse.data.body ?? completeResponse.data;
            let result;
            try { result = typeof completeData === 'string' ? JSON.parse(completeData) : completeData; }
            catch (_) { throw new Error('Failed to parse completion response'); }

            if (completeResponse.status === 200 && !result.error) {
                logThroughput(estimatedTotal, startedAt);
                setProgress(100);
                return true;
            }
            throw new Error('Failed to finalize upload: ' + (result.error || completeResponse.status));
        } catch (error) {
            if (isCancel(error)) return false;
            setProgressLabel('Upload failed');
            showToast(friendlyUploadError(error), "error");
            return false;
        } finally {
            abortControllerRef.current = null;
        }
    };

    const logThroughput = (bytes, startedAt) => {
        const secs = (Date.now() - startedAt) / 1000;
        const mbps = ((bytes * 8) / 1e6 / secs).toFixed(1);
        console.log(`[upload] done: ${(bytes / 1048576).toFixed(1)} MB in ${secs.toFixed(0)}s = ${mbps} Mbps`);
    };

    // A single, already-zipped upload should be sent as-is rather than re-zipped.
    // Detect by extension because browsers report .zip MIME types inconsistently
    // (application/zip, application/x-zip-compressed, or empty).
    const isPreZipped = () =>
        droppedFiles.length === 1 &&
        (getFileExtension(droppedFiles[0].name) === '.zip' || fileType === 'application/zip');

    const onDrop = useCallback((newFiles) => {
        if (!newFiles.length) return;

        const rejected = newFiles.filter(f => validateFile(f));
        const accepted = newFiles.filter(f => !validateFile(f));
        if (rejected.length) {
            showToast(`Removed ${rejected.length} file(s) that exceed the 5GB size limit.`, "error");
        }
        if (!accepted.length) return;

        let temp_dict = {};
        accepted.forEach((item) => {
            const relPath = item.webkitRelativePath || item.path || item.name;
            const parent_name = relPath.includes('/') ? relPath.split('/')[0] : item.name;
            if (temp_dict[parent_name]) {
                temp_dict[parent_name].push(relPath);
            } else {
                temp_dict[parent_name] = [relPath];
            }
        });

        if (Object.keys(temp_dict).length > 0) {
            setDroppedFilesDict(prev => ({
                ...prev,
                ...temp_dict
            }));
        }

        setDroppedFiles(prevFiles => [...prevFiles, ...accepted]);
    }, [showToast]);

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        noClick: true,
    });

    // Logic to build parent child structure
    const buildTree = (paths) => {
        const tree = {};
        paths.forEach((p) => {
            const parts = p.split('/');
            let current = tree;
            parts.forEach((part, i) => {
                if (i === parts.length - 1) {
                    if (!current.__files__) current.__files__ = [];
                    current.__files__.push(part);
                } else {
                    if (!current[part]) current[part] = {};
                    current = current[part];
                }
            });
        });
        return tree;
    };

    const [expandedFolders, setExpandedFolders] = React.useState({});

    const toggleFolder = (path) => {
        setExpandedFolders(prev => ({ ...prev, [path]: prev[path] === false }));
    };

    const fileRemoveFolder = (folderPath) => {
        const prefix = folderPath + '/';
        setDroppedFiles(prev => prev.filter(f => {
            const p = f.webkitRelativePath || f.path || f.name;
            return !p.startsWith(prefix) && p !== folderPath;
        }));

        setDroppedFilesDict(prev => {
            const updated = {};
            for (const [folder, paths] of Object.entries(prev)) {
                const filtered = paths.filter(p => !p.startsWith(prefix) && p !== folderPath);
                if (filtered.length > 0) {
                    updated[folder] = filtered;
                }
            }
            return updated;
        });
    };

    const FolderTree = ({ tree, depth = 0, parentPath = '', onRemoveRoot }) => {
        const folders = Object.keys(tree).filter(k => k !== '__files__');
        const files = tree.__files__ || [];

        return (
            <>
                {folders.map((folder) => {
                    const fullPath = parentPath ? `${parentPath}/${folder}` : folder;
                    const isExpanded = expandedFolders[fullPath] !== false;
                    const subTree = tree[folder];
                    const subFolders = Object.keys(subTree).filter(k => k !== '__files__');
                    const subFiles = subTree.__files__ || [];
                    const totalItems = subFolders.length + subFiles.length;
                    const isRoot = depth === 0;

                    return (
                        <div key={fullPath}>
                            <div
                                style={{ display: 'flex', alignItems: 'center', padding: '4px 0', paddingLeft: `${depth * 20}px`, cursor: 'pointer' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }} onClick={() => toggleFolder(fullPath)}>
                                    {isExpanded ? <BsChevronDown size={12} style={{ marginRight: '6px', flexShrink: 0 }}/> : <BsChevronRight size={12} style={{ marginRight: '6px', flexShrink: 0 }}/>}
                                    <BsFolderFill size={20} color="#1F4F59" style={{ marginRight: '8px', flexShrink: 0 }}/>
                                    <span style={{ fontSize: '14px', color: '#1F4F59', fontWeight: 500 }}>{folder}</span>
                                    <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>({totalItems})</span>
                                </div>
                                <BsTrash size={20} color="#999" style={{ cursor: 'pointer', flexShrink: 0, padding: '2px' }} onClick={(e) => { e.stopPropagation(); isRoot && onRemoveRoot ? onRemoveRoot() : fileRemoveFolder(fullPath); }}/>
                            </div>
                            {isExpanded && <FolderTree tree={subTree} depth={depth + 1} parentPath={fullPath}/>}
                        </div>
                    );
                })}
                {files.map((file) => {
                    const filePath = parentPath ? `${parentPath}/${file}` : file;
                    return (
                        <div key={filePath} style={{ display: 'flex', alignItems: 'center', padding: '3px 0', paddingLeft: `${depth * 20}px` }}>
                            <BsFileEarmarkCheck size={20} color="#1F4F59" style={{ marginRight: '8px', flexShrink: 0 }}/>
                            <span style={{ fontSize: '13px', color: '#444', flex: 1 }}>{file}</span>
                            <BsXLg size={20} color="#999" style={{ cursor: 'pointer', flexShrink: 0, padding: '2px' }} onClick={() => fileRemoveSingle(filePath)}/>
                        </div>
                    );
                })}
            </>
        );
    };

    const handleFileSubmit = async (event) => {
        event.preventDefault();

        setProgress(1);
        setProgressLabel(' Processing...');

        const fileId = generateRandomString(12);

        const formData = {
            id: fileId || null,
            practice_name: sanitizeInput(event.target['practice_name'].value),
            practitioner_name: sanitizePathName(event.target['practitioner_name'].value.replace(/Dr\. |dr\. |Dr |dr /g, '')),
            contact_email: sanitizeInput(event.target['contact_email'].value),
            office_number: null,
            patient_name: sanitizePathName(event.target['patient_name'].value),
            patient_dob: patient_dob_date ? new Date(patient_dob_date).toISOString().slice(0, 10).replace('/', '_') : '1999-01-01',
            patient_gender: null,
            patient_ethnicity: null,
            patient_scan_date: patient_scan_date ? new Date(patient_scan_date).toISOString().slice(0, 10).replace('/', '_') : '1999-01-01',
            report: sanitizeInput(event.target['report'].value),
            report_plus: sanitizeInput(event.target['report_plus'].value),
            payment_method: sanitizeInput(event.target['payment_method'].value),
            regions_of_interest: sanitizeInput(regions_of_interest),
            file_name: buildZipName(event.target['patient_name'].value, event.target['practitioner_name'].value),
            file_type: 'application/zip',
            dttm: new Date(),
            user_ip: ip
        }

        const safePatientName = sanitizePathName(event.target['patient_name'].value);
        const safePractitionerName = sanitizePathName(event.target['practitioner_name'].value.replace(/Dr\. |dr\. |Dr |dr /g, ''));

        // Upload the file to S3 FIRST. We only record the referral once the file
        // is safely stored, so a failed/cancelled upload never leaves an orphaned
        // referral with no submitted file.
        let uploaded;
        if (isPreZipped()) {
            // Already a single .zip on disk — validate, then upload as-is. Disk-backed
            // Blob slices stream without loading the whole file into memory.
            const zippedFiles = droppedFiles[0];
            let isValidZip = false;
            try { isValidZip = await validateZipMagicBytes(zippedFiles); } catch (_) {}
            if (!isValidZip) {
                showToast("Invalid zip file. The file appears to be corrupted or is not a valid zip archive.", "error");
                setProgress(0);
                setProgressLabel('');
                return;
            }
            uploaded = await uploadFile(fileId, safePatientName, safePractitionerName, zippedFiles);
        } else {
            // Zip a folder and stream it into multipart parts so a 3-4GB archive
            // never has to fit in the tab's memory at once.
            setProgress(1);
            setProgressLabel('Preparing files...');
            uploaded = await uploadFolderStreaming(fileId, safePatientName, safePractitionerName);
        }
        if (!uploaded) return; // upload path already showed a toast / handled cancel

        // Now record the referral metadata.
        setProgressLabel('Saving referral...');
        try {
            const response = await axios.post(API_URLS.SUBMISSION, formData);
            const status = response.data?.statusCode || response.status;
            if (status !== 200) {
                let errorMsg = "Unknown error";
                try {
                    if (response.data?.body) errorMsg = JSON.parse(response.data.body)?.error || errorMsg;
                } catch (_) {}
                showToast(`Your files uploaded, but the referral could not be recorded: ${errorMsg}. Please contact us so we don't miss it.`, "error");
                setProgress(0);
                setProgressLabel('');
                return;
            }
        } catch (error) {
            let errorMsg = error.message;
            try {
                if (error.response?.data?.body) {
                    errorMsg = JSON.parse(error.response.data.body)?.error || errorMsg;
                }
            } catch (_) {}
            showToast(`Your files uploaded, but the referral could not be recorded: ${errorMsg}. Please contact us so we don't miss it.`, "error");
            setProgress(0);
            setProgressLabel('');
            return;
        }

        setProgress(100);
        setProgressLabel('Submitted');
    };

    return (
        <div id="body">
            {toast.show && (
                <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
                    <div className="toast-content">
                        {toast.type === 'success' && <BsCheckCircle className="toast-icon" />}
                        {toast.type === 'error' && <BsXCircle className="toast-icon" />}
                        {toast.type === 'info' && <BsInfoCircle className="toast-icon" />}
                        <span>{toast.message}</span>
                    </div>
                </div>
            )}
            <div className="page">
                <div className="navbar" style={{justifyContent: 'flex-start', paddingLeft: 'clamp(16px, 5vw, 48px)'}}>
                    <img alt="Carolina OMF Imaging" style={{height: 'clamp(36px, 5vh, 60px)', width: 'auto', padding: '8px 0'}} src={logo}/>
                </div>
                <div className="form-page-content">
                    {progress === 0 ?
                        <>
                            <label htmlFor="formFile" id="patientReferralTitle" className="form-label">Patient Referral Information</label>
                            <form className="form-two-panel" encType="multipart/form-data" onSubmit={handleFileSubmit}>
                                <div className="form-left-panel">
                                    <div className="form-field-grid">
                                        <div>
                                            <label id="formSubLabel">Practice Name *</label>
                                            <input id="chat-input" name="practice_name" placeholder="Practice Name" maxLength="200" value={practice_name} onChange={(e) => setPracticeName(e.target.value)} required/>
                                        </div>
                                        <div>
                                            <label id="formSubLabel">Practitioner Name *</label>
                                            <input id="chat-input" name="practitioner_name" placeholder="Practitioner Name" maxLength="100" value={practitioner_name} onChange={(e) => setPractitionerName(e.target.value)} required/>
                                        </div>
                                        <div>
                                            <label id="formSubLabel">Contact Email (Report and Invoice sent here) *</label>
                                            <input id="chat-input" name="contact_email" type='email' placeholder="Contact Email" maxLength="100" value={contact_email} onChange={(e) => setContactEmail(e.target.value)} required/>
                                        </div>
                                        {/* Office Number field hidden — formData sends null */}
                                        <div>
                                            <label id="formSubLabel">Patient Name *</label>
                                            <input id="chat-input" name="patient_name" placeholder="Patient Name" maxLength="100" value={patient_name} onChange={(e) => setPatientName(e.target.value)} required/>
                                        </div>
                                        <div>
                                            <label id="formSubLabel">Patient Date of Birth</label>
                                            <DatePicker oneTap format="MM/dd/yyyy" name="patient_dob" onChange={(date) => { setPatientDOBDate(new Date(date)); setDobManuallySet(true); }} onSelect={handlePatientDOBDateChange} placeholder="mm/dd/yyyy" block />
                                        </div>
                                        {/* Gender and Ethnicity fields hidden — formData sends null */}
                                        <div>
                                            <label id="formSubLabel">Patient Scan Date</label>
                                            <DatePicker oneTap format="MM/dd/yyyy" name="patient_scan_date" onChange={(date) => { setPatientScanDate(new Date(date)); setScanDateManuallySet(true); }} onSelect={handlePatientScanDateChange} placeholder="mm/dd/yyyy" block/>
                                        </div>
                                        <div>
                                            <label id="formSubLabel">Report</label>
                                            <select id="dropdown-input" name="report" value={studyValue} onChange={handleStudyChange}>
                                                {studyOptions.map((option) => (<option key={option.label} value={option.value}>{option.label}</option>))}
                                            </select>
                                        </div>
                                        <div>
                                            <label id="formSubLabel">Report Plus</label>
                                            <select id="dropdown-input" name="report_plus" value={studyPlusValue} onChange={handleStudyPlusChange}>
                                                {studyPlusOptions.map((option) => (<option key={option.label} value={option.value}>{option.label}</option>))}
                                            </select>
                                        </div>
                                        <div>
                                            <label id="formSubLabel">Preferred Payment Method</label>
                                            <select id="dropdown-input" name="payment_method" value={paymentMethod} onChange={handlePaymentMethod}>
                                                {paymentMethodOptions.map((option) => (<option key={option.label} value={option.value}>{option.label}</option>))}
                                            </select>
                                        </div>
                                    </div>
                                    <label id="formSubLabel">Regions of Interest</label>
                                    <textarea id="regions-input" name="regions_of_interest" placeholder="Regions of Interest / Clinical Signs / Symptoms" value={regions_of_interest} onChange={handleRegionsChange} style={{flex: 1}}/>
                                </div>
                                <div className="form-right-panel">
                                    <label id="formSubLabel">Upload non-proprietary DCM patient data *</label>
                                    <div className={`fileDrop${isDragActive ? ' fileDrop-active' : ''}`} style={{flex: 1, height: 'auto'}} {...getRootProps()}>
                                        <div style={{display: 'block', textAlign: 'center'}}>
                                            <BsCloudUpload size={35} color="#1F4F59" className="fileDrop-icon" style={{margin: '5px'}}/>
                                            <label style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2px'}}>
                                                {isDragActive ? 'Drop to add files or folders' : 'Drag & drop files, folders, or a .zip to upload'}
                                            </label>
                                            <div style={{display: "flex", gap: '10px', justifyContent: 'center', alignItems: "center"}}>
                                                <div>
                                                    <input id="file_picker" className="form-control" style={{display: "none"}} name="file" multiple type="file" onChange={handleFileInput}/>
                                                    <label className="btn" id="pickerFile" htmlFor={"file_picker"}>Browse files</label>
                                                </div>
                                                <div>
                                                    <input id="folder_picker" className="form-control" style={{display: "none"}} name="file" multiple directory="" webkitdirectory="" type="file" onChange={handleFolderInput}/>
                                                    <label className="btn" id="pickerFile" htmlFor={"folder_picker"}>Browse folder</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {Object.values(droppedFilesDict).some(files => files.length > 0) &&
                                        <>
                                            <div style={{display: "flex", justifyContent: "flex-end", marginBottom: '6px'}}>
                                                <span id="fileDisplayRemove" onClick={() => fileRemoveAll()}>
                                                    <BsTrash size={20}/> Remove all files
                                                </span>
                                            </div>
                                            <div className="fileDisplay" style={{ padding: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                                                {Object.entries(droppedFilesDict).map(([folder, paths]) => {
                                                    const tree = buildTree(paths);
                                                    return (
                                                        <div key={folder} style={{ marginBottom: '8px' }}>
                                                            <FolderTree tree={tree} onRemoveRoot={() => fileRemove(folder)} />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    }
                                    <label style={{marginTop: '10px', display: 'block'}}>*Reports will take 3-5 business days to complete</label>
                                    <button className="btn" id="submitFile" type="submit" disabled={!isFormValid()}>Submit</button>
                                </div>
                            </form>
                        </>
                    :
                        progressLabel === 'Upload failed' ?
                            <div style={{flex: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 20px"}}>
                                <div style={{display: "flex", flexDirection: "column", alignItems: "center", maxWidth: "450px", width: "100%"}}>
                                    <div style={{backgroundColor: "#f8d7da", borderRadius: "50%", padding: "20px", marginBottom: "10px"}}>
                                        <BsXCircle size={40} color="#1A2A45"/>
                                    </div>
                                    <h2 style={{color: "#1F4F59", fontSize: "24px", fontWeight: "600", margin: "0 0 12px 0"}}>Upload Failed</h2>
                                    <p style={{color: "#666", fontSize: "14px", textAlign: "center", marginBottom: "24px"}}>
                                        The file upload did not complete. This can happen with large files on slower connections. Please try again.
                                    </p>
                                    <button className="btn" style={{background: '#1F4F59', color: '#fff', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '500'}}
                                        onClick={() => { setProgress(0); setProgressLabel(''); }}>
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        :
                        progressLabel !== 'Submitted' ?
                            <div className="loading-overlay" style={{position: 'fixed'}}>
                                <div className="loading-logo-wrap">
                                    <svg className="loading-svg" viewBox="-2 0 204 82" xmlns="http://www.w3.org/2000/svg">
                                        <g className="wave-line wave-line-1">
                                            <path fill="#1A2A45" d="M 199.78 34.52 L 199.78 42.1 C 199.3 41.98 198.82 41.85 198.35 41.72 C 186.89 38.51 179.0 34.11 170.85 29.57 C 162.19 24.73 153.23 19.73 138.8 15.58 C 115.09 8.74 96.14 14.26 74.58 23.22 C 70.77 24.8 66.88 26.49 62.87 28.24 C 62.57 28.37 62.27 28.5 61.97 28.63 C 54.68 31.81 47.14 35.1 38.93 38.3 C 32.82 40.69 27.04 42.68 21.64 44.34 C 13.52 46.84 6.27 48.58 -0.03 49.81 L -0.03 42.43 C 9.49 40.46 21.23 37.23 34.77 31.95 C 39.71 30.02 44.43 28.05 49.01 26.09 C 51.86 24.86 54.66 23.64 57.43 22.44 C 58.8 21.84 60.15 21.25 61.5 20.67 C 87.91 9.22 111.36 0.08 142.03 8.91 C 157.62 13.41 167.51 18.92 176.26 23.79 C 183.6 27.88 190.38 31.68 199.78 34.52"/>
                                        </g>
                                        <g className="wave-line wave-line-2">
                                            <path fill="#8A9BAE" d="M 199.78 41.76 L 199.78 48.88 C 196.69 48.82 193.65 48.72 190.65 48.58 C 147.44 46.77 113.86 39.31 86.32 33.19 C 77.94 31.33 70.1 29.59 62.87 28.24 C 57.96 27.31 53.34 26.56 49.01 26.09 C 46.16 25.76 43.44 25.54 40.85 25.47 C 26.5 25.06 11.82 28.06 -0.03 31.52 L -0.03 23.88 C 12.1 20.59 26.64 17.94 41.19 18.35 C 47.43 18.53 54.2 19.38 61.5 20.67 C 65.69 21.4 70.04 22.26 74.58 23.22 C 79.15 24.18 83.92 25.24 88.86 26.35 C 117.46 32.7 152.65 40.52 198.35 41.72 C 198.82 41.74 199.31 41.75 199.78 41.76"/>
                                        </g>
                                        <g className="wave-line wave-line-3">
                                            <path fill="#A4D4E6" d="M 199.78 63.33 L 199.78 70.51 C 196.1 70.73 192.09 70.87 187.81 70.87 C 178.99 70.87 169.04 70.29 158.59 68.62 C 150.63 67.36 143.52 66.06 136.71 64.79 C 135.2 64.5 133.7 64.23 132.21 63.94 C 128.22 63.19 124.2 62.43 119.99 61.67 C 102.78 58.57 82.46 55.44 48.13 52.47 C 33.71 51.24 14.52 51.34 -0.03 51.71 L -0.03 44.57 C 6.54 44.4 14.03 44.29 21.64 44.34 C 31.14 44.39 40.82 44.69 49.15 45.4 C 92.47 49.13 113.79 53.15 134.39 57.03 C 135.02 57.14 135.65 57.27 136.29 57.38 C 141.84 58.43 147.58 59.49 153.76 60.54 C 155.92 60.92 158.15 61.29 160.45 61.66 C 175.22 64.0 189.09 64.01 199.78 63.33"/>
                                        </g>
                                        <g className="wave-line wave-line-4">
                                            <path fill="#1F4F59" d="M 190.65 48.59 C 183.82 49.1 176.34 49.77 168.61 50.61 C 159.24 51.64 148.15 54.29 136.29 57.38 C 133.4 58.13 130.47 58.91 127.52 59.69 C 125.04 60.35 122.53 61.02 119.99 61.68 C 97.94 67.46 73.86 73.2 52.43 72.55 C 31.03 71.91 12.27 64.76 -0.03 58.64 L -0.03 67.07 C 13.07 73.03 31.26 79.05 52.08 79.67 C 53.3 79.7 54.53 79.72 55.78 79.72 C 80.38 79.72 106.95 72.67 130.52 66.43 C 132.61 65.87 134.67 65.32 136.71 64.79 C 142.64 63.23 148.37 61.76 153.76 60.54 C 159.63 59.22 165.08 58.19 169.9 57.66 C 180.74 56.48 191.09 55.65 199.78 55.08 L 199.78 47.94 C 196.91 48.12 193.86 48.33 190.65 48.59"/>
                                        </g>
                                    </svg>
                                    <div className="loading-progress-bar-track">
                                        <div className="loading-progress-bar-fill" style={{ width: `${progress}%` }}></div>
                                    </div>
                                    <p className="loading-label">{progressLabel.trim()}</p>
                                    {progressLabel !== 'Finalizing...' && progressLabel !== 'Preparing files...' && (
                                        <button
                                            onClick={cancelUpload}
                                            style={{
                                                marginTop: '16px', background: 'transparent', border: '1px solid #1F4F59',
                                                color: '#1F4F59', padding: '8px 24px', borderRadius: '6px', fontSize: '13px',
                                                fontWeight: '500', cursor: 'pointer'
                                            }}
                                        >
                                            Cancel Upload
                                        </button>
                                    )}
                                </div>
                            </div>
                        :
                            <div style={{flex: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 20px"}}>
                                <div style={{display: "flex", flexDirection: "column", alignItems: "center", maxWidth: "450px", width: "100%"}}>
                                    <div style={{backgroundColor: "#A4D4E6", borderRadius: "50%", padding: "20px", marginBottom: "10px"}}>
                                        <FaCheck size={40} color="#1A2A45"/>
                                    </div>
                                    <h2 style={{color: "#1F4F59", fontSize: "24px", fontWeight: "600", margin: "0 0 30px 0"}}>Referral Submitted</h2>
                                    <div style={{width: "100%", backgroundColor: "#f8f9fa", borderRadius: "10px", padding: "24px", marginBottom: "24px"}}>
                                        {/* <div style={{display: "flex", justifyContent: "space-between", marginBottom: "12px"}}>
                                            <span style={{color: "#666", fontSize: "14px"}}>Email receipt sent to</span>
                                            <span style={{color: "#1F4F59", fontSize: "14px", fontWeight: "500"}}>{contact_email}</span>
                                        </div> */}
                                        <div style={{display: "flex", justifyContent: "space-between", }}>
                                            <span style={{color: "#666", fontSize: "14px"}}>Contact Us</span>
                                            <a href="mailto:ncolvin@carolinaomfimaging.com" style={{color: "#1F4F59", fontSize: "14px", fontWeight: "500", textDecoration: "none"}}>ncolvin@carolinaomfimaging.com</a>
                                        </div>
                                    </div>
                                    <div style={{backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "14px 20px", width: "100%", textAlign: "center"}}>
                                        <span style={{color: "#1A2A45", fontSize: "14px", fontWeight: "500"}}>Expect your report in 3-5 Business Days</span>
                                    </div>
                                </div>
                            </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default PatientReferralForm;
